import { memo, useState, useEffect, useMemo, useCallback } from 'react'
import { Image } from '@/atoms/Image'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'

export const EyederWrapper = memo(() => {
  const [showStealingEyeder, setShowStealingEyeder] = useState(false)
  const [showGlaringEyeder, setShowGlaringEyeder] = useState(true)

  // Memoize image URLs to prevent recalculation
  const imageUrls = useMemo(
    () => ({
      eyederRight1: getCloudinaryImageUrl({
        height: 100,
        path: `v1753808807/angel-studios/projects/sketch/eyeder/Eyeder_Still_Corner_No_Shadow_Blank_01_2_1_-small-2.webp`,
        transforms: 'q_auto:best',
      }),
      eyederRight2: getCloudinaryImageUrl({
        height: 100,
        path: `v1753808807/angel-studios/projects/sketch/eyeder/Eyeder_Still_Corner_No_Shadow_Blank_01_2_1_-small-1.webp`,
        transforms: 'q_auto:best',
      }),
      eyederLeft1: getCloudinaryImageUrl({
        height: 100,
        path: 'v1753808807/angel-studios/projects/sketch/eyeder/Eyeder_Still_Wall_Glare_No_Shadow_Blank_01_1_copy-small-1.webp',
        transforms: 'q_auto:best',
      }),
      eyederLeft2: getCloudinaryImageUrl({
        height: 100,
        path: 'v1753808807/angel-studios/projects/sketch/eyeder/Eyeder_Still_Wall_Glare_No_Shadow_Blank_01_1_copy-small-2.webp',
        transforms: 'q_auto:best',
      }),
    }),
    [],
  )

  // Memoize random interval function
  const getRandomInterval = useCallback(() => 2000 + Math.random() * 6000, [])

  // Image preloading effect
  useEffect(() => {
    Object.values(imageUrls).forEach((url) => {
      const img = new window.Image()
      img.src = url
    })
  }, [imageUrls])

  // Combined timer logic to reduce effect overhead
  useEffect(() => {
    let stealingTimeoutId: NodeJS.Timeout
    let glaringTimeoutId: NodeJS.Timeout

    const scheduleStealingSwitch = () => {
      stealingTimeoutId = setTimeout(() => {
        setShowStealingEyeder((prev) => !prev)
        scheduleStealingSwitch()
      }, getRandomInterval())
    }

    const scheduleGlaringSwitch = () => {
      glaringTimeoutId = setTimeout(() => {
        setShowGlaringEyeder((prev) => !prev)
        scheduleGlaringSwitch()
      }, getRandomInterval())
    }

    scheduleStealingSwitch()
    scheduleGlaringSwitch()

    return () => {
      clearTimeout(stealingTimeoutId)
      clearTimeout(glaringTimeoutId)
    }
  }, [getRandomInterval])

  // Memoize class calculations
  const rightImageClasses = useMemo(
    () => ({
      visible:
        'absolute -right-3.5 -top-3 -z-50 rotate-[330deg] transition-opacity duration-200 ease-in-out opacity-100',
      hidden:
        'absolute -right-3.5 -top-3 -z-50 rotate-[330deg] transition-opacity duration-200 ease-in-out opacity-0 pointer-events-none',
    }),
    [],
  )

  const leftImageClasses = useMemo(
    () => ({
      visible:
        'absolute -left-2.5 -top-2 -z-50 rotate-[340deg] transition-opacity duration-200 ease-in-out opacity-100',
      hidden:
        'absolute -left-2.5 -top-2 -z-50 rotate-[340deg] transition-opacity duration-200 ease-in-out opacity-0 pointer-events-none',
    }),
    [],
  )

  return (
    <>
      <Image
        aria-label="Sketch Eyeder Right"
        alt="Sketch Eyeder Right"
        src={imageUrls.eyederRight1}
        height={44}
        width={26}
        className={showStealingEyeder ? rightImageClasses.visible : rightImageClasses.hidden}
        loading="lazy"
      />
      <Image
        aria-label="Sketch Eyeder Right"
        alt="Sketch Eyeder Right"
        src={imageUrls.eyederRight2}
        height={44}
        width={26}
        className={showStealingEyeder ? rightImageClasses.hidden : rightImageClasses.visible}
        loading="lazy"
      />
      <Image
        aria-label="Sketch Eyeder Left"
        alt="Sketch Eyeder Left"
        src={imageUrls.eyederLeft1}
        height={30}
        width={50}
        className={showGlaringEyeder ? leftImageClasses.visible : leftImageClasses.hidden}
        loading="lazy"
      />
      <Image
        aria-label="Sketch Eyeder Left"
        alt="Sketch Eyeder Left"
        src={imageUrls.eyederLeft2}
        height={30}
        width={50}
        className={showGlaringEyeder ? leftImageClasses.hidden : leftImageClasses.visible}
        loading="lazy"
      />
    </>
  )
})

EyederWrapper.displayName = 'EyederWrapper'
